import ApiService from '@zola-helpers/client/dist/es/http/api';
import { toastsActions } from '@zola-helpers/client/dist/es/redux/toasts';
import { handleDeleteEventErrors } from 'actions/EventActions';
import type {
  WCmsPageEntityView,
  WSuggestedFaqView,
  WCmsMealOptionView,
} from '@zola/svc-web-api-ts-client';
import * as ActionType from 'actions/types/website/EntityActionTypes';
import type { EntityActionTypes, GenericEntityType } from 'actions/types/website/EntityActionTypes';
import type { AppThunk, RootState } from 'reducers/index';

// selectors
import { getEntitiesById, getEntitiesOrderedIds } from 'selectors/website/entitySelectors';
import { filterEntitiesOrderedIds } from 'util/filterEntities';

export const TRAVEL_TYPES: Record<string, string> = {
  HOTEL: 'Hotel',
  HOME_RENTAL: 'House or Rental',
  SHUTTLE: 'Shuttle',
  FLIGHT: 'Flight',
  CAR_RENTAL: 'Rental Car',
  TRAIN: 'Train',
  NOTE: 'Travel Note',
};

const ENTITY_TYPES: Record<string, string> = {
  POI: 'Recommendation',
  RSVP: 'Event',
  WEDDING_PARTY: 'Wedding Party Member',
  FAQ: 'FAQ',
  TRAVEL: 'Travel',
  HOME: 'Story',
  PHOTO: 'Photo',
  VIDEO: 'Video',
  EVENT: 'Event',
  REGISTRY: 'Registry',
};

export function updateEntityRequest(): EntityActionTypes {
  return {
    type: ActionType.ENTITY_REQUESTED,
  };
}

export function receiveEntityResponse(response: GenericEntityType): EntityActionTypes {
  return {
    type: ActionType.ENTITY_CREATED,
    payload: { entity: response },
  };
}

export function receiveUpdatedEntityResponse(response: GenericEntityType): EntityActionTypes {
  return {
    type: ActionType.ENTITY_RECEIVED,
    payload: { entity: response },
  };
}

export function getEntitiesRequest(): EntityActionTypes {
  return {
    type: ActionType.ENTITIES_REQUESTED,
  };
}

export function getEntitiesResponse(
  entities: GenericEntityType[],
  mealOptions?: Array<WCmsMealOptionView>
): EntityActionTypes {
  return {
    type: ActionType.ENTITIES_RECEIVED,
    payload: { entities, mealOptions },
  };
}

export function editEntityRequest(entity: GenericEntityType): EntityActionTypes {
  return {
    type: ActionType.ENTITY_SELECTED,
    payload: { id: entity.id },
  };
}

export function entityDeleted(id: number): EntityActionTypes {
  return {
    type: ActionType.ENTITY_DELETED,
    payload: { id },
  };
}

export function entityMoved(response: WCmsPageEntityView[]): EntityActionTypes {
  return {
    type: ActionType.ENTITY_MOVED,
    payload: { entities: response },
  };
}

export function resetEntities(): EntityActionTypes {
  return { type: ActionType.ENTITIES_RESET };
}

export function addEntity(): EntityActionTypes {
  return {
    type: ActionType.ENTITY_ADD,
  };
}

export function requestSuggestions(): EntityActionTypes {
  return {
    type: ActionType.SUGGESTIONS_REQUESTED,
  };
}

export function receiveSuggestions(data: WSuggestedFaqView[]): EntityActionTypes {
  return {
    type: ActionType.SUGGESTIONS_RECEIVED,
    payload: data,
  };
}

export const whichEntityIds = (state: RootState, entityName?: string): number[] =>
  entityName
    ? filterEntitiesOrderedIds(getEntitiesOrderedIds(state), getEntitiesById(state), entityName)
    : getEntitiesOrderedIds(state);

export function reorderEntities(entityIds: number[]): AppThunk<Promise<void>> {
  return (dispatch, getState): Promise<void> => {
    const requestBody = {
      entityIds,
      pageId: getState().website.pages.activePage.id,
    };
    return ApiService.post<Array<WCmsPageEntityView>>(
      '/web-api/v1/cms/page/pages/list/reorder',
      requestBody
    )
      .then(json => {
        dispatch(entityMoved(json));
      })
      .catch();
  };
}

export function moveEntity(
  oldIndex: number,
  newIndex: number,
  entityName?: string
): AppThunk<void> {
  return (dispatch, getState): void => {
    const orderedIds = whichEntityIds(getState(), entityName);
    dispatch({
      type: ActionType.ENTITY_MOVE,
      payload: { oldIndex, newIndex, entityName, orderedIds },
    });
    dispatch(reorderEntities(orderedIds));
  };
}

export function moveSpecificEntity(
  oldIndex: number,
  newIndex: number,
  specificEntities: number[],
  entityName?: string
): AppThunk<void> {
  return (dispatch, getState): void => {
    const orderedIds = whichEntityIds(getState(), entityName).filter(id =>
      specificEntities.includes(id)
    );
    dispatch({
      type: ActionType.ENTITY_MOVE,
      payload: { oldIndex, newIndex, entityName, orderedIds },
    });
    dispatch(reorderEntities(orderedIds));
  };
}

export function deleteEntityV2(entity: GenericEntityType): AppThunk<Promise<void>> {
  return (dispatch, getState): Promise<void> => {
    dispatch({ type: ActionType.ENTITY_DELETE, payload: { id: entity.id } });
    const { activePage } = getState().website.pages;
    const requestBody = {
      entityId: entity.id,
      pageId: activePage.id,
    };
    let entityType: string;
    if (activePage.type === 'TRAVEL') {
      const { type } = entity;
      if (type) {
        entityType = TRAVEL_TYPES[type];
      }
    } else if (activePage.type === 'PHOTO') {
      const { image_id } = entity;
      entityType = image_id ? 'Photo' : 'Video';
    } else if (activePage.type) {
      entityType = ENTITY_TYPES[activePage.type];
    }
    return ApiService.post('/web-api/v2/cms/entity/delete', requestBody)
      .then(response => {
        if (activePage.type === 'EVENT' && entity.uuid) {
          handleDeleteEventErrors(response, entity.uuid, dispatch);
        }
      })
      .then(() => {
        if (entity.id) {
          dispatch(entityDeleted(entity.id));
        }
      })
      .then(() => {
        dispatch(toastsActions.positive({ headline: `${entityType} Removed` }));
      });
  };
}

export function requestAddress(): EntityActionTypes {
  return {
    type: ActionType.REQUEST_ADDRESS,
  };
}

export function receiveAddress(
  response: Record<string, unknown>,
  overrideName: string
): EntityActionTypes {
  return {
    type: ActionType.RECEIVE_ADDRESS,
    payload: response,
    overrideName,
  };
}

export function resetAddress(): EntityActionTypes {
  return {
    type: ActionType.RESET_ADDRESS,
  };
}
